import React, { useEffect, useState } from "react";

interface House {
  uid: string;
  name: string;
  max: number;
  current: number;
}

const House: React.FC = () => {
  const [transferDetails, setTransferDetails] = useState<House[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editData, setEditData] = useState<
    { max: string; current: string; uid: string }[]
  >([]);
  const [updatingUid, setUpdatingUid] = useState<string | null>(null);
  const [mode, setMode] = useState(localStorage.getItem("mode") || "manual");
  const [loading, setLoading] = useState(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [selectedHouseAction, setSelectedHouseAction] = useState<string | null>(
    localStorage.getItem("houseAction") || null
  );

  useEffect(() => {
    fetchTransferDetails();

    if (mode === "automatic") {
      const interval = setInterval(fetchTransferDetails, 3000); // Refresh every 3 seconds in automatic mode
      setIntervalId(interval);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [mode]);

  const fetchTransferDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/gethouse`);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data: House[] = await response.json();
      setTransferDetails(data);
      const initialEditData = data.map(({ uid, max, current }) => ({
        uid,
        max: max.toString(),
        current: current.toString(),
      }));
      setEditData(initialEditData);
    } catch (error) {
      console.error(`Error fetching transfer details:`, error);
      setTransferDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (
    uid: string,
    field: "max" | "current",
    value: string
  ) => {
    setEditData(
      editData.map((item) =>
        item.uid === uid ? { ...item, [field]: value } : item
      )
    );
  };

  const handleEdit = async (uid: string) => {
    setUpdatingUid(uid);
    const item = editData.find((item) => item.uid === uid);
    if (!item) {
      console.error("Edit data not found");
      setUpdatingUid(null);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/updatehouse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, max: item.max, current: item.current }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      setTransferDetails(
        transferDetails.map((house) =>
          house.uid === uid
            ? {
              ...house,
              max: parseInt(item.max),
              current: parseInt(item.current),
            }
            : house
        )
      );
    } catch (error) {
      console.error("Error updating house details:", error);
    } finally {
      setUpdatingUid(null);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredDepositData = transferDetails.filter((item) => {
    const uidStr = item.uid.toString();
    const searchTermLower = searchTerm.toLowerCase();
    return (
      uidStr.includes(searchTerm) ||
      item.name.toLowerCase().includes(searchTermLower)
    );
  });

  const handleModeChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newMode = event.target.value as "manual" | "automatic";
    setMode(newMode);
    localStorage.setItem("mode", newMode);

    if (intervalId) clearInterval(intervalId);

    const url =
      newMode === "manual"
        ? `${process.env.REACT_APP_BASE_URL_API}/stop-house`
        : `${process.env.REACT_APP_BASE_URL_API}/start-house`;

    try {
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error("Network response was not ok");

      console.log(`${newMode} mode activated:`, await response.json());
    } catch (error) {
      console.error(`Failed to activate ${newMode} mode:`, error);
    }

    if (newMode === "automatic") {
      const interval = setInterval(fetchTransferDetails, 3000);
      setIntervalId(interval);
    }
  };

  const handleResetHouse = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/reset-house`, {
        method: "GET",
      });
      if (!response.ok) throw new Error("Network response was not ok");
      console.log("House reset successfully:", await response.json());
      window.location.reload();
    } catch (error) {
      console.error("Failed to reset house:", error);
    }
  };

  const handleWinHouse = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL_API}/win-house`, {
        method: "GET",
      });
      if (!response.ok) throw new Error("Network response was not ok");
      console.log("House reset successfully:", await response.json());
      window.location.reload();
    } catch (error) {
      console.error("Failed to reset house:", error);
    }
  };
  const handleHouseAction = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const action = event.target.value; // "reset-house" or "win-house"
    setSelectedHouseAction(action);
    localStorage.setItem("houseAction", action);
    const url =
      action === "reset-house"
        ? `${process.env.REACT_APP_BASE_URL_API}/reset-house`
        : `${process.env.REACT_APP_BASE_URL_API}/win-house`;

    try {
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error("Network response was not ok");

      console.log(`${action} executed:`, await response.json());
      window.location.reload(); // Refresh the page after success
    } catch (error) {
      console.error(`Failed to execute ${action}:`, error);
    }
  };

  return (
    <div className="ml-0 lg:ml-[230px] mt-4 bg-[#F5F5F4] pl-4 md:pl-0">

      <div className="flex items-center justify-between p-4 bg-[#F9FAFB] dark:bg-[#F9FAFB] rounded-t-lg">
        <h2 className="text-xl font-bold text-black">{`Game Controller`}</h2>
        <div className=" flex justify-end items-center">
          <label htmlFor="search" className="sr-only">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name or UID"
              value={searchTerm}
              onChange={handleSearchChange}
              className="bg-[#F9FAFB] w-full pl-10 pr-4 py-2 rounded-lg border  shadow-sm focus:outline-none text-black"
              disabled={mode === "automatic"}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-500"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap justify-around bg-white items-center p-2">
        <div className="flex flex-wrap bg-[#ffffff] shadow-md  p-1 rounded-lg w-34 sm:w-72 text-sm mt-4">
          <label className="flex-1 text-center cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="manual"
              className="hidden"
              checked={mode === "manual"}
              onChange={handleModeChange}
            />
            <span
              className={`whitespace-nowrap text-black flex items-center justify-center p-2 rounded-lg transition-all duration-150 ease-in-out font-semibold ${mode === "manual"
                  ? "bg-[#228BE6] text-white"
                  : "hover:bg-[#ffffff]"
                }`}
            >
              Manual
            </span>
          </label>
          <label className="flex-1 text-center cursor-pointer">
            <input
              type="radio"
              name="mode"
              value="automatic"
              className="hidden"
              checked={mode === "automatic"}
              onChange={handleModeChange}
            />
            <span
              className={`whitespace-nowrap text-black flex items-center justify-center p-2 rounded-lg transition-all duration-150 ease-in-out font-semibold ${mode === "automatic"
                  ? "bg-[#228BE6] text-white"
                  : "hover:bg-[#F9FAFB]"
                }`}
            >
              Automatic
            </span>
          </label>
        </div>
        <div className="flex flex-wrap bg-[#F9FAFB] p-1 rounded-lg shadow-sm w-36 sm:w-72 text-sm mt-4">
          <label className="flex-1 text-center cursor-pointer">
            <input
              type="radio"
              name="houseAction"
              value="reset-house"
              className="hidden"
              checked={selectedHouseAction === "reset-house"}
              onChange={handleHouseAction}
            />
            <span
              className={`whitespace-nowrap text-black flex items-center justify-center p-2 rounded-lg transition-all duration-150 ease-in-out font-semibold ${selectedHouseAction === "reset-house"
                  ? "bg-[#228BE6] text-white"
                  : "hover:bg-[#F9FAFB]"
                }`}
            >
              Reset
            </span>
          </label>
          <label className="flex-1 text-center cursor-pointer">
            <input
              type="radio"
              name="houseAction"
              value="win-house"
              className="hidden"
              checked={selectedHouseAction === "win-house"}
              onChange={handleHouseAction}
            />
            <span
              className={`whitespace-nowrap text-black flex items-center justify-center p-2 rounded-lg transition-all duration-150 ease-in-out font-semibold ${selectedHouseAction === "win-house"
                  ? "bg-[#228BE6] text-white"
                  : "hover:bg-[#F9FAFB]"
                }`}
            >
              Win
            </span>
          </label>
        </div>
      </div>
      <div
        className={`pt-5 h-[690px] rounded-sm overflow-scroll custom-scrollbar bg-white ${mode === "manual" ? "" : "opacity-50"
          }`}
      >
        <table className="w-full text-sm text-left text-gray-500 ">
          <thead className="!text-[0.875rem] text-[#49556D] bg-[#FFFFFF]">
            <tr>
              <th scope="col" className="py-3 px-6 ">
                S.No.
              </th>
              <th scope="col" className="py-3 px-6 ">
                UID
              </th>
              <th scope="col" className="py-3 px-6 ">
                Name
              </th>
              <th scope="col" className="py-3 px-6 ">
                MAX
              </th>
              <th scope="col" className="py-3 px-6 ">
                Current
              </th>
              <th scope="col" className="py-3 px-6 ">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDepositData.map((request, index) => (
              <tr key={request.uid}>
                <td className="text-[#495057] group bg-[#F9FAFB] border-b border-gray-200 px-6">
                  {index + 1}
                </td>
                <td className="text-[#495057] group bg-[#F9FAFB] border-b border-gray-200">
                  {request.uid}
                </td>
                <td className="text-[#495057] group bg-[#F9FAFB] border-b border-gray-200">
                  {request.name}
                </td>
                <td className="text-[#495057] group bg-[#F9FAFB] border-b border-gray-200">
                  <input
                    type="number"
                    defaultValue={request.max}
                    disabled={
                      loading ||
                      mode === "automatic" ||
                      updatingUid === request.uid
                    }
                    onChange={(e) =>
                      handleEditChange(request.uid, "max", e.target.value)
                    }
                    className="bg-[#F9FAFB] w-[90%] p-1 pl-10 pr-4 py-2 rounded-lg border  shadow-sm focus:outline-none text-[#495057]"
                  />
                </td>
                <td className="group bg-[#F9FAFB] border-b border-gray-200">
                  <input
                    type="number"
                    defaultValue={request.current}
                    disabled={
                      loading ||
                      mode === "automatic" ||
                      updatingUid === request.uid
                    }
                    onChange={(e) =>
                      handleEditChange(request.uid, "current", e.target.value)
                    }
                    className="bg-[#F9FAFB] w-[90%] p-1 pl-10 pr-4 py-2 rounded-lg border  shadow-sm focus:outline-none text-[#495057]"
                  />
                </td>
                <td className="py-3 px-8 bg-[#F9FAFB] border-b border-gray-200">
                  <button
                    onClick={() => handleEdit(request.uid)}
                    disabled={
                      loading ||
                      mode === "automatic" ||
                      updatingUid === request.uid
                    }
                    className={`py-2 px-4 rounded ${updatingUid === request.uid
                        ? "bg-gray-500 text-white"
                        : "bg-[#228BE6] text-white"
                      }`}
                  >
                    {updatingUid === request.uid ? "Updating..." : "Update"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && (
          <p className="text-center mt-4">Updating details, please wait...</p>
        )}
      </div>
    </div>
  );
};

export default House;
